import React, { useEffect } from "react";
import { Lock } from "lucide-react";
import { useApp } from "../context/AppContext";

/* 分公司范围：所属分公司为 la / norcal / sd（非总部、非 admin / ceo）的账号 → 记账 / 区域 / 小组下拉只显示本分公司，且只剩一个选项时锁定显示 */
export const TEAM_REGION = { team_norcal: "norcal", team_1: "la", team_2: "la", team_3: "la", team_sd: "sd" };
export const REGION_TEAMS = { la: ["team_1", "team_2", "team_3"], norcal: ["team_norcal"], sd: ["team_sd"] };
export const REGION_LABELS = { la: { zh: "洛杉矶", en: "Los Angeles" }, norcal: { zh: "北加州", en: "NorCal" }, sd: { zh: "圣地亚哥", en: "San Diego" } };
const WILDCARDS = new Set(["", "all", "__all__"]);

export function scopeOf(user) {
  const r = user?.region;
  if (!r || !REGION_LABELS[r] || ["admin", "ceo"].includes(user?.role)) return null;
  return { region: r, regions: [r], teams: REGION_TEAMS[r] || [] };
}

export function useScope() {
  const { user } = useApp();
  return scopeOf(user);
}

export const scopeAllowed = (scope, kind) => (!scope ? null : kind === "team" ? scope.teams : scope.regions);

/* 过滤任意选项列表：kind=region 按 la/norcal/sd；kind=team 按 team_*；无范围原样返回 */
export function scopeFilter(scope, list, kind = "region", getKey = (x) => x.id ?? x.key ?? x.value ?? x) {
  const allowed = scopeAllowed(scope, kind);
  if (!allowed) return list;
  return list.filter((x) => allowed.includes(String(getKey(x))));
}

/* 原生 <select> 的范围版：子节点 <option> 按范围过滤；只剩一个有效选项时锁定为标签并自动回填 value */
export function ScopeSelect({ kind = "region", value, onChange, children, className, lockLabel, ...rest }) {
  const scope = useScope();
  const { lang } = useApp();
  const zh = lang !== "en";
  const allowed = scopeAllowed(scope, kind);
  const kids = React.Children.toArray(children).flatMap((c) => (c?.type === React.Fragment ? React.Children.toArray(c.props.children) : [c]));
  const opts = allowed ? kids.filter((c) => c?.props && allowed.includes(String(c.props.value))) : kids;
  const wild = allowed && opts.length > 1 ? kids.filter((c) => c?.props && WILDCARDS.has(String(c.props.value))) : [];
  const single = allowed && opts.length === 1 ? opts[0] : (allowed && opts.length === 0 ? { props: { value: kind === "team" ? scope.teams[0] || scope.region : scope.region, children: (REGION_LABELS[scope.region] || {})[zh ? "zh" : "en"] || scope.region } } : null);
  const target = single ? String(single.props.value) : null;
  useEffect(() => { if (target !== null && String(value ?? "") !== target) onChange?.({ target: { value: target } }); }, [target]); // eslint-disable-line
  if (single) {
    return (
      <div data-testid={rest["data-testid"]} data-locked="1" data-value={target} title={zh ? "已按所属分公司锁定" : "Locked to your branch"}
        className={`${className || ""} inline-flex items-center gap-1 bg-slate-50 text-slate-700 font-semibold cursor-not-allowed select-none`}>
        <Lock className="h-3 w-3 text-slate-400 shrink-0" />{lockLabel || single.props.children}
      </div>);
  }
  return <select value={value} onChange={onChange} className={className} {...rest}>{allowed ? [...wild, ...opts] : kids}</select>;
}
